import type { Command } from '../../types/commands'
import { colorize, formatHighlight, formatCommand } from '../colorScheme'

export const tutorialCommand: Command = {
name: 'tutorial',
description: 'Interactive tutorial for new users',
usage: 'tutorial [start|skip]',
aliases: ['guide', 'learn'],
category: 'system',
execute: (args) => {
const action = args[0]?.toLowerCase()

if (action === 'skip') {
localStorage.setItem('tutorial-completed', 'true')
return [
{
type: 'success',
content: '✓ Tutorial marked as complete.\n\nYou can restart it anytime with: tutorial start',
},
]
}

const isCompleted = localStorage.getItem('tutorial-completed') === 'true'

if (action === 'start' || !isCompleted) {
const tutorialText = `
╔═══════════════════════════════════════════════════════════╗
║            ${formatHighlight('WELCOME TO THE TUTORIAL!')}                       ║
╚═══════════════════════════════════════════════════════════╝

${colorize('👋 Hello!', 'brightCyan')} This interactive portfolio is built like a terminal.
If you've never used a terminal before, don't worry - this guide
will teach you everything you need to know!

╭─ ${colorize('LESSON 1: Basic Commands', 'yellow')} ──────────────────────────────╮
│                                                         │
│  Commands are text instructions you type and execute    │
│  by pressing ${colorize('Enter', 'cyan')}.                                     │
│                                                         │
│  ${colorize('Try this now:', 'brightYellow')}                                          │
│  1. Type: ${formatCommand('about')}                                         │
│  2. Press ${colorize('Enter', 'cyan')}                                         │
│                                                         │
│  This shows information about Anderson. Simple, right?  │
│                                                         │
╰─────────────────────────────────────────────────────────╯

╭─ ${colorize('LESSON 2: Getting Help', 'yellow')} ──────────────────────────────╮
│                                                           │
│  The ${formatCommand('help')} command shows all available commands.       │
│                                                           │
│  ${colorize('Try it:', 'brightYellow')}                                                    │
│  • Type: ${formatCommand('help')}                                         │
│  • For detailed info: ${formatCommand('help [command]')}                  │
│    Example: ${formatCommand('help projects')}                             │
│                                                           │
╰───────────────────────────────────────────────────────────╯

╭─ ${colorize('LESSON 3: Exploring Files', 'yellow')} ───────────────────────────╮
│                                                           │
│  This portfolio organizes content like a file system.    │
│                                                           │
│  ${colorize('Key commands:', 'brightYellow')}                                              │
│  • ${formatCommand('ls')} - List files in current location              │
│  • ${formatCommand('cat [file]')} - Read a file                         │
│  • ${formatCommand('cd [folder]')} - Move to a folder                   │
│                                                           │
│  ${colorize('Try this sequence:', 'brightYellow')}                                         │
│  1. ${formatCommand('ls')} - See what's available                        │
│  2. ${formatCommand('cd thoughts')} - Enter the blog folder             │
│  3. ${formatCommand('ls')} - See blog posts                              │
│  4. ${formatCommand('cat [filename]')} - Read a post                    │
│  5. ${formatCommand('cd ..')} - Go back to home                          │
│                                                           │
╰───────────────────────────────────────────────────────────╯

╭─ ${colorize('LESSON 4: Helpful Features', 'yellow')} ──────────────────────────╮
│                                                           │
│  ${colorize('Tab Autocomplete:', 'cyan')} Press ${colorize('Tab', 'yellow')} to auto-complete commands │
│  and file names. Try typing "pro" then ${colorize('Tab', 'yellow')}!            │
│                                                           │
│  ${colorize('Command History:', 'cyan')} Use ${colorize('↑', 'yellow')} and ${colorize('↓', 'yellow')} arrow keys to cycle  │
│  through previous commands. Very handy!                  │
│                                                           │
│  ${colorize('Clear Input:', 'cyan')} Press ${colorize('Escape', 'yellow')} to clear what you've typed │
│  without executing it.                                   │
│                                                           │
╰───────────────────────────────────────────────────────────╯

╭─ ${colorize('LESSON 5: Accessibility', 'yellow')} ─────────────────────────────╮
│                                                           │
│  This terminal is built with accessibility in mind.      │
│                                                           │
│  ${colorize('Customize your experience:', 'brightYellow')}                                 │
│  • ${formatCommand('settings font large')} - Bigger text                │
│  • ${formatCommand('settings contrast high')} - High contrast mode      │
│  • ${formatCommand('settings')} - See all accessibility options         │
│                                                           │
│  All your preferences are saved automatically!           │
│                                                           │
╰───────────────────────────────────────────────────────────╯

╭─ ${colorize('🎓 QUICK REFERENCE', 'brightCyan')} ──────────────────────────────────╮
│                                                           │
│  ${colorize('Start exploring:', 'yellow')}                                            │
│  • ${formatCommand('about')} - Learn about Anderson                      │
│  • ${formatCommand('projects')} - See portfolio projects                 │
│  • ${formatCommand('skills')} - View technical skills                    │
│  • ${formatCommand('contact')} - Get in touch                            │
│  • ${formatCommand('blog')} - Read blog posts                            │
│                                                           │
│  ${colorize('Need help?', 'yellow')}                                               │
│  • ${formatCommand('help')} - List all commands                          │
│  • ${formatCommand('help [command]')} - Detailed command info            │
│  • ${formatCommand('tutorial')} - Re-read this tutorial                  │
│                                                           │
╰───────────────────────────────────────────────────────────╯

${colorize('🎉 You\'re ready to explore!', 'green')}

${colorize('Type', 'brightBlack')} ${formatCommand('tutorial skip')} ${colorize('to mark this tutorial as complete', 'brightBlack')}
${colorize('and hide it on future visits.', 'brightBlack')}
      `

      return [
        {
          type: 'info',
          content: <div dangerouslySetInnerHTML={{ __html: tutorialText }} />,
        },
      ]
    }

    // If tutorial is already completed and no specific action
    const completedText = `
${colorize('Tutorial Status:', 'cyan')} ${colorize('✓ Completed', 'green')}

You can:
• Type ${formatCommand('tutorial start')} to review the tutorial again
• Type ${formatCommand('help')} to see all available commands
• Type ${formatCommand('help [command]')} for detailed command info
    `

    return [
      {
        type: 'info',
        content: <div dangerouslySetInnerHTML={{ __html: completedText }} />,
      },
    ]
  },
}
